import { type JSX, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Spinner } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

type Status = 'verifying' | 'success' | 'invalid' | 'error';

/**
 * Verification landing page. The :token URL param carries the raw
 * token from the verification email link; this page POSTs it to
 * /v1/auth/verify-email on mount and renders the result.
 *
 * Three terminal states:
 *   - success → confirmation copy + link to /login
 *   - invalid → "link expired or used" + CTA to log in (where the
 *     user can hit "Resend verification")
 *   - error   → generic transient error + retry hint
 *
 * No form or button — verification is one-shot on page load. The
 * useRef guard prevents React 18 StrictMode's double-mount from
 * double-firing the request (single-use token would 400 the second
 * call and confuse the UI).
 */
export default function VerifyEmailPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('verifying');
  const fired = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    if (fired.current) return;
    fired.current = true;

    void (async () => {
      try {
        await api.post<void>('/v1/auth/verify-email', { token });
        setStatus('success');
      } catch (err) {
        if (err instanceof ApiError && err.code === 'invalid_token') {
          setStatus('invalid');
        } else {
          setStatus('error');
        }
      }
    })();
  }, [token]);

  return (
    <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
      <div className="w-full max-w-[400px] text-center">
        {status === 'verifying' && (
          <>
            <Spinner />
            <p className="text-[14px] text-text-3 mt-4">Verifying your email…</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="inline-flex w-12 h-12 rounded-full bg-success-bg items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-success">
                <path
                  d="M5 12.5L10 17.5L19 7.5"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 className="display text-[24px] font-semibold tracking-[-0.02em] text-text leading-[1.2] m-0">
              Email verified
            </h1>
            <p className="text-[14.5px] text-text-3 mt-2.5 mb-6 leading-relaxed">
              Thanks. Your email is confirmed and you can sign in.
            </p>
            <Link
              to="/login"
              className="inline-block bg-volt text-volt-ink font-medium px-5 py-2.5 rounded-md text-[14px] hover:bg-volt-600"
            >
              Continue to sign in
            </Link>
          </>
        )}

        {status === 'invalid' && (
          <>
            <h1 className="display text-[24px] font-semibold tracking-[-0.02em] text-text leading-[1.2] m-0">
              This link is invalid or expired
            </h1>
            <p className="text-[14.5px] text-text-3 mt-2.5 mb-6 leading-relaxed">
              Sign in and click "Resend verification" from your account settings to get a fresh link.
            </p>
            <Link
              to="/login"
              className="text-[14px] text-volt-500 font-medium hover:underline"
            >
              Go to sign in
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="display text-[24px] font-semibold tracking-[-0.02em] text-text leading-[1.2] m-0">
              Something went wrong
            </h1>
            <p className="text-[14.5px] text-text-3 mt-2.5 mb-6 leading-relaxed">
              We couldn't verify your email right now. Sign in and click "Resend verification" to try again with a fresh link.
            </p>
            <Link
              to="/login"
              className="text-[14px] text-volt-500 font-medium hover:underline"
            >
              Go to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
